import request from "@/lib/api/handler";
import { useAuth0 } from "@auth0/auth0-react";
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { QueryKeys } from "@/queries/utils";
import { useProfileQuery } from "@/queries/user/profile";

import { ConversationMessage } from "@/queries/chat/user-chat";
import { ConversationSummary } from "@/queries/chat/user-conversation";

export const useSendChatMessage = (conversationId: string) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { profile } = useProfileQuery();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (data: { message: string; clientId: string }) => {
      const token = await getAccessTokenSilently();

      return request<ConversationMessage>({
        method: "POST",
        path: `/api/chat/${conversationId}/messages`,
        token,
        body: JSON.stringify(data),
      });
    },
    onMutate: async ({
      message,
      clientId,
    }: {
      message: string;
      clientId: string;
    }) => {
      await qc.cancelQueries({
        queryKey: QueryKeys.chatMessage(conversationId),
      });

      const now = new Date().toISOString();

      const optimisticMessage: ConversationMessage = {
        id: clientId,
        clientId,
        message,
        senderId: profile?.id ?? "me",
        senderName: profile?.name,
        avatarLink: profile?.profilePictureLink,
        createdAt: now,
        updatedAt: now,
        isOptimistic: true,
      };

      qc.setQueryData<ConversationSummary[]>(
        QueryKeys.chatConversations,
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          return oldData.map((conversation) =>
            conversation.id === conversationId
              ? {
                  ...conversation,
                  lastMessage: optimisticMessage,
                  lastSeenAt: now,
                  lastSeenMessageId: clientId,
                }
              : conversation,
          );
        },
      );

      qc.setQueryData(
        QueryKeys.chatMessage(conversationId),
        (oldData?: InfiniteData<ConversationMessage[]>) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            pages: [
              [optimisticMessage, ...(oldData.pages[0] ?? [])],
              ...oldData.pages.slice(1),
            ],
          };
        },
      );

      return { clientId };
    },
    onError: (_, __, context) => {
      if (!context) {
        return;
      }

      qc.setQueryData(
        QueryKeys.chatMessage(conversationId),
        (oldData?: InfiniteData<ConversationMessage[]>) => {
          if (!oldData) {
            return;
          }

          // If the mutation fails, remove the optimistically added message
          const pages = oldData.pages.map((page) =>
            page.filter((msg) => msg.clientId === context.clientId),
          );

          return {
            ...oldData,
            pages,
          };
        },
      );
    },
    onSuccess: (data, _, context) => {
      // Update lastMessage in chatConversations summary (if newer)
      qc.setQueryData<ConversationSummary[]>(
        QueryKeys.chatConversations,
        (oldConversations) =>
          // Map over conversations
          oldConversations?.map((conversation) =>
            // If we find the same conversation
            conversation.id === conversationId &&
            // And we have a more recent lastMessage
            (!conversation.lastMessage?.createdAt ||
              data.createdAt > conversation.lastMessage.createdAt ||
              // Or we are replacing the optimistic message
              conversation.lastMessage.clientId === data.clientId)
              ? // Update the lastMessage field
                { ...conversation, lastMessage: data }
              : // Otherwise, make no change
                conversation,
          ),
      );

      // Replace optimistic message in chatMessage cache
      qc.setQueryData(
        QueryKeys.chatMessage(conversationId),
        (oldData?: InfiniteData<ConversationMessage[]>) => {
          if (!oldData) return;

          return {
            ...oldData,
            pages: oldData.pages.map((page, i) => {
              // Only update the first page (latest messages)
              if (i !== 0) return page;

              return page.map((message) =>
                message.clientId === context.clientId ? data : message,
              );
            }),
          };
        },
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: QueryKeys.chatMessage(conversationId),
      });
    },
  });

  return {
    sendMessage: (message: string) =>
      mutate({ message, clientId: `temp-${new Date().getTime()}` }),
    isSendingMessage: isPending,
    sendMessageError: error,
  };
};
